/**
 * Highway Code Reader — searchable, category-filtered library.
 *
 * Mobile-first. List view by default. Tap to expand a rule.
 * Search box debounces on input.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, ArrowLeft, Loader2, BookOpen, ChevronDown } from "lucide-react";
import BrowserNarration from "../components/BrowserNarration";
import { Input } from "../components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

export default function HighwayCodeReader() {
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [rules, setRules] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [expandedKey, setExpandedKey] = useState(null);

  // Load categories once.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/highway-code/categories`);
        setCategories(data.categories || []);
      } catch { /* ignore */ }
    })();
  }, []);

  // Debounced rules fetch on category/search change.
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    setExpandedKey(null);
    setLoading(true);
    setLoadError(false);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (activeCat) params.set("category", activeCat);
        if (q.trim()) params.set("q", q.trim());
        const { data } = await axios.get(`${API}/api/highway-code/rules?${params}`, { signal: controller.signal });
        if (alive) setRules(data.rules || []);
      } catch {
        if (alive) { setRules([]); setLoadError(true); }
      } finally {
        if (alive) setLoading(false);
      }
    }, 220);
    return () => { alive = false; clearTimeout(t); controller.abort(); };
  }, [activeCat, q, retry]);

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-16">
      <div className="sticky top-0 z-10 bg-slate-50/95 dark:bg-[#0c1120]/95 backdrop-blur border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="hc-back"
            onClick={() => nav("/learn")}
            className="text-slate-500 hover:text-slate-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <BookOpen className="w-4 h-4 text-amber-600" />
          <h1 className="font-display text-base md:text-lg font-bold text-slate-900 dark:text-white">
            Highway Code
          </h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-5">
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="hc-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search rules — e.g. roundabout, motorway, fog…"
            className="pl-10 h-12 bg-white dark:bg-slate-900"
          />
        </div>

        {/* Category chips */}
        <div className="flex gap-2 mb-5 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-thin">
          <CategoryChip on={!activeCat} onClick={() => setActiveCat(null)} testid="cat-all">
            All ({categories.reduce((s, c) => s + (c.count || 0), 0)})
          </CategoryChip>
          {categories.map((c) => (
            <CategoryChip
              key={c.slug}
              on={activeCat === c.slug}
              onClick={() => setActiveCat(c.slug)}
              testid={`cat-${c.slug}`}
            >
              {c.title} ({c.count})
            </CategoryChip>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
          </div>
        ) : loadError ? (
          <div role="alert"><p>The rules could not be loaded.</p><button onClick={() => setRetry(n => n + 1)}>Retry</button></div>
        ) : rules.length === 0 ? (
          <div className="text-center text-sm text-slate-500 py-10">
            No rules match. Try a different search.
          </div>
        ) : (
          <ul className="space-y-2">
            {rules.map((r) => {
              const open = expandedKey === r.key;
              return (
                <li
                  key={r.key}
                  data-testid={`hc-rule-${r.key}`}
                  className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl"
                >
                  <button
                    onClick={() => setExpandedKey(open ? null : r.key)}
                    className="w-full text-left px-4 py-3 flex items-start gap-3"
                  >
                    <span className="text-[10px] font-extrabold uppercase tracking-wider bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded px-2 py-0.5 mt-0.5 flex-shrink-0 tabular-nums">
                      {r.rule_number}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-[15px] font-semibold text-slate-900 dark:text-white leading-tight">
                        {r.title}
                      </div>
                      <div className="text-[12.5px] text-slate-500 dark:text-slate-400 mt-1 leading-snug">
                        {r.summary}
                      </div>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-slate-400 mt-1 flex-shrink-0 transition ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-4 pb-4 pt-1 text-[13.5px] text-slate-700 dark:text-slate-200 leading-relaxed border-t border-slate-100 dark:border-slate-800">
                      {r.body}
                      <BrowserNarration key={r.key} title={`Highway Code ${r.rule_number}: ${r.title}`} text={[r.rule_number, r.title, r.body || r.summary].filter(Boolean).join(". ")} />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function CategoryChip({ children, on, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex-shrink-0 text-[12px] font-bold rounded-full px-3 py-1.5 transition ${
        on
          ? "bg-amber-500 text-slate-900"
          : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300"
      }`}
    >
      {children}
    </button>
  );
}
